import Header from "../../../components/feature/Header";
import Footer from "../../../components/feature/Footer";
import { Link } from "react-router-dom";
import { mockProjects } from "../../../mocks/projects";

const residentialProjects = mockProjects.filter((p) => p.category === "Residential");

const capabilities = [
  { icon: "ri-home-4-line", title: "New Custom Homes", desc: "From modest infill to expansive estates — fully custom residential design from the ground up." },
  { icon: "ri-add-box-line", title: "Additions & Extensions", desc: "Expand your existing home with additions that feel original to the architecture." },
  { icon: "ri-mountain-line", title: "Hillside & Coastal", desc: "Specialized expertise in complex sites, sloped terrain, and coastal regulatory requirements." },
  { icon: "ri-home-smile-line", title: "ADU / Second Units", desc: "Accessory dwelling units and garage conversions for rental income or multigenerational living." },
];

export default function ResidentialService() {
  return (
    <div className="bg-arch-cream font-sans">
      <Header />
      <main>
        {/* Hero */}
        <section className="relative h-[60vh] min-h-[460px] flex items-end overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://public.readdy.ai/ai/img_res/07cd40cf31b419900c7ea3a4b4b95d86.jpg"
              alt="Residential Architecture Studio 1323"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-black/10" />
          </div>
          <div className="relative z-10 px-8 lg:px-14 pb-14 w-full">
            <nav className="flex items-center gap-2 mb-4 text-white/50 text-xs">
              <Link to="/services" className="hover:text-white transition-colors cursor-pointer">Services</Link>
              <i className="ri-arrow-right-s-line" />
              <span className="text-white/80">Residential Architecture</span>
            </nav>
            <h1 className="font-serif text-4xl lg:text-6xl text-white font-light leading-tight max-w-2xl">
              Residential<br />Architecture
            </h1>
          </div>
        </section>

        {/* Overview */}
        <section className="py-16 lg:py-20 px-8 lg:px-14 bg-white">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-start">
            <div>
              <p className="text-arch-light text-xs tracking-[0.2em] uppercase mb-4">Residential Architecture</p>
              <h2 className="font-serif text-3xl lg:text-4xl text-arch-charcoal font-light leading-tight mb-6">
                Homes designed for<br />how you actually live
              </h2>
              <p className="text-arch-gray text-sm font-light leading-relaxed mb-4">
                Studio 1323 designs custom homes across Santa Monica, Brentwood, Pacific Palisades, Malibu, and greater Los Angeles. Every project begins with a deep understanding of how our clients live — their daily routines, social patterns, and long-term needs.
              </p>
              <p className="text-arch-gray text-sm font-light leading-relaxed mb-8">
                We work across styles and scales — from compact urban infill to sprawling coastal estates — always guided by site, light, and the specific preferences of each client. Our residential architecture is designed to endure: aesthetically, structurally, and functionally.
              </p>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 bg-arch-charcoal text-white px-7 py-3.5 text-xs tracking-[0.15em] uppercase font-semibold hover:bg-arch-black transition-all rounded-md cursor-pointer whitespace-nowrap"
              >
                Start a Residential Project <i className="ri-arrow-right-line" />
              </Link>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {capabilities.map((cap) => (
                <div key={cap.title} className="bg-arch-cream p-5 rounded-lg border border-arch-border">
                  <div className="w-9 h-9 flex items-center justify-center mb-3 border border-arch-border rounded-md">
                    <i className={`${cap.icon} text-arch-charcoal text-base`} />
                  </div>
                  <h3 className="text-arch-charcoal text-sm font-semibold mb-1">{cap.title}</h3>
                  <p className="text-arch-gray text-xs font-light leading-relaxed">{cap.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Projects */}
        {residentialProjects.length > 0 && (
          <section className="py-16 px-8 lg:px-14 bg-arch-cream border-t border-arch-border">
            <p className="text-arch-light text-xs tracking-[0.2em] uppercase mb-8">Residential Projects</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {residentialProjects.map((p) => (
                <Link key={p.id} to={`/projects/${p.id}`} className="group block cursor-pointer">
                  <div className="w-full h-64 overflow-hidden rounded-lg mb-4 relative">
                    <img src={p.image} alt={p.title} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-600" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                    <div className="absolute bottom-4 left-5 right-5">
                      <p className="text-white/60 text-xs tracking-wider uppercase mb-1">{p.location} · {p.year}</p>
                      <h3 className="font-serif text-xl text-white font-light">{p.title}</h3>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* CTA */}
        <section className="py-16 px-8 lg:px-14 bg-[#111111] flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
          <div>
            <h2 className="font-serif text-3xl text-white font-light">Planning a residential project?</h2>
            <p className="text-white/50 text-sm font-light mt-2">Let&apos;s discuss your home — initial consultations are complimentary.</p>
          </div>
          <Link to="/contact" className="inline-flex items-center gap-2 bg-white text-arch-charcoal px-7 py-3.5 text-xs tracking-[0.15em] uppercase font-semibold hover:bg-white/90 transition-all rounded-md cursor-pointer whitespace-nowrap">
            Request Consultation <i className="ri-arrow-right-line" />
          </Link>
        </section>
      </main>
      <Footer />
    </div>
  );
}
